import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import apiService from '../services/apiService';
import {
  ContactContainer,
  ContactHero,
  Container,
  PageTitle,
  PageSubtitle,
  ContactContent,
  ContactGrid,
  ContactInfo,
  ContactDetails,
  ContactItem,
  ContactForm,
  FormGroup,
  SubmitButton,
  MapSection,
  SectionTitle,
  SectionSubtitle,
  MapContainer,
  SuccessMessage,
  ErrorMessage
} from '../styles/ContactStyles';

const Contact = () => {
  const [contactContent, setContactContent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const contact = await apiService.getContactContent();
        setContactContent(contact);
      } catch (error) {
        console.error('Error fetching contact content:', error);
        // Set default content if API fails
        setContactContent({
          address: 'Monrovia, Liberia',
          phone: '+231-XXX-XXXX',
          email: 'info@cocoapro.com',
          hours: 'Monday - Friday: 8:00 AM - 6:00 PM'
        });
      }
    };

    fetchContent();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitStatus(null);

    try {
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

      if (!serviceId || !templateId || !publicKey) {
        throw new Error('EmailJS environment variables are missing. Please add them to your .env file.');
      }

      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        reply_to: formData.email,
        to_name: 'Cocoapro Team'
      };

      await emailjs.send(serviceId, templateId, templateParams, { publicKey });

      setSubmitStatus({
        type: 'success',
        message: 'Thank you for your message! We will get back to you soon.'
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Sorry, there was an error sending your message. Please try again.'
      });
      console.error('EmailJS error:', error);
    } finally {
      setSubmitting(false);
    }
  };



  return (
    <ContactContainer>
      {/* Hero Section */}
      <ContactHero>
        <Container>
          <PageTitle>Contact Us</PageTitle>
          <PageSubtitle>
            Get in touch with us to learn more about our services and how we can help your business
          </PageSubtitle>
        </Container>
      </ContactHero>

      {/* Contact Content */}
      <ContactContent>
        <Container>
          <ContactGrid>
            {/* Contact Information */}
            <ContactInfo>
              <h2>Get In Touch</h2>
              <p>
                We'd love to hear from you. Whether you have questions about our services, 
                need agricultural consulting, or want to discuss a partnership, our team is 
                here to help.
              </p>
              
              <ContactDetails>
                <ContactItem>
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3>Address</h3>
                    <p>{contactContent?.address || 'Monrovia, Liberia'}</p>
                  </div>
                </ContactItem>

                <ContactItem>
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  <div>
                    <h3>Phone</h3>
                    <p>{contactContent?.phone || '+231-XXX-XXXX'}</p>
                  </div>
                </ContactItem>

                <ContactItem>
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  <div>
                    <h3>Email</h3>
                    <p>{contactContent?.email || 'info@cocoapro.com'}</p>
                  </div>
                </ContactItem>

                <ContactItem>
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h3>Business Hours</h3>
                    <p>{contactContent?.hours || 'Monday - Friday: 8:00 AM - 6:00 PM'}</p>
                  </div>
                </ContactItem>
              </ContactDetails>
            </ContactInfo>

            {/* Contact Form */}
            <ContactForm onSubmit={handleSubmit}>
              <h2>Send Us a Message</h2>
              
              {submitStatus && (
                submitStatus.type === 'success' ? (
                  <SuccessMessage>{submitStatus.message}</SuccessMessage>
                ) : (
                  <ErrorMessage>{submitStatus.message}</ErrorMessage>
                )
              )}

              <FormGroup>
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Your full name"
                  required
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="email">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your.email@example.com"
                  required
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+231-XXX-XXXX"
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="subject">Subject *</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder="What is this regarding?"
                  required
                />
              </FormGroup>

              <FormGroup>
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Tell us more about your inquiry..."
                  required
                />
              </FormGroup>

              <SubmitButton type="submit" disabled={submitting}>
                {submitting ? 'Sending...' : 'Send Message'}
              </SubmitButton>
            </ContactForm>
          </ContactGrid>
        </Container>
      </ContactContent>

      {/* Map Section */}
      <MapSection>
        <Container>  
          <SectionTitle>Find Us</SectionTitle>
          <SectionSubtitle>
            Visit our facilities in Monrovia, Liberia
          </SectionSubtitle>
          
          <MapContainer>
            <iframe
              title="Monrovia, Liberia - Map"
              src="https://www.google.com/maps?q=Monrovia%2C%20Liberia&z=12&output=embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </MapContainer>
        </Container>
      </MapSection>
    </ContactContainer>
  );
};

export default Contact;
